const ANALYTICS_ENDPOINT = "/api/event-page-analytics";
const SESSION_STORAGE_KEY = "rhp_event_page_session_id";
const UTM_STORAGE_KEY = "rhp_event_page_utm";
const DEDUPE_PREFIX = "rhp_event_page_analytics_dedupe";
const DEDUPE_TTL_MS = 5000;
const IS_DEV = process.env.NODE_ENV !== "production";
const ALLOWED_EVENT_TYPES = new Set([
  "page_view",
  "cta_click",
  "rsvp_start",
  "rsvp_submit",
  "thank_you_view",
  "sunset_view",
]);
const DEDUPED_EVENT_TYPES = new Set([
  "page_view",
  "rsvp_start",
  "thank_you_view",
  "sunset_view",
]);
const memoryDedupe = new Map();

function canUseBrowserApis() {
  return typeof window !== "undefined" && typeof navigator !== "undefined";
}

function coerceString(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function trimToNull(value) {
  const next = coerceString(value);
  return next || null;
}

function debugLog(...args) {
  if (!IS_DEV) return;
  console.log("[event-page-analytics]", ...args);
}

function debugWarn(...args) {
  if (!IS_DEV) return;
  console.warn("[event-page-analytics]", ...args);
}

function buildFallbackId() {
  return `eps_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function createSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return buildFallbackId();
}

function readJson(storage, key) {
  try {
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(storage, key, value) {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    return;
  }
}

function getSessionId() {
  if (!canUseBrowserApis()) return createSessionId();

  try {
    const existing = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;

    const next = createSessionId();
    window.localStorage.setItem(SESSION_STORAGE_KEY, next);
    return next;
  } catch {
    return createSessionId();
  }
}

function getDeviceType(userAgent = "") {
  const ua = coerceString(userAgent).toLowerCase();
  if (!ua) return "desktop";

  if (
    /ipad|tablet|playbook|silk|kindle/.test(ua) ||
    (/android/.test(ua) && !/mobile/.test(ua))
  ) {
    return "tablet";
  }

  if (/iphone|ipod|android|blackberry|iemobile|opera mini|mobile/.test(ua)) {
    return "mobile";
  }

  return "desktop";
}

function persistUtmParams() {
  if (!canUseBrowserApis()) return {};

  const searchParams = new URLSearchParams(window.location.search);
  const next = {
    utm_source: trimToNull(searchParams.get("utm_source")),
    utm_medium: trimToNull(searchParams.get("utm_medium")),
    utm_campaign: trimToNull(searchParams.get("utm_campaign")),
    utm_term: trimToNull(searchParams.get("utm_term")),
    utm_content: trimToNull(searchParams.get("utm_content")),
  };

  const existing = readJson(window.localStorage, UTM_STORAGE_KEY) ||
    readJson(window.sessionStorage, UTM_STORAGE_KEY) ||
    {};

  const merged = {
    utm_source: next.utm_source || existing.utm_source || null,
    utm_medium: next.utm_medium || existing.utm_medium || null,
    utm_campaign: next.utm_campaign || existing.utm_campaign || null,
    utm_term: next.utm_term || existing.utm_term || null,
    utm_content: next.utm_content || existing.utm_content || null,
  };

  writeJson(window.localStorage, UTM_STORAGE_KEY, merged);
  writeJson(window.sessionStorage, UTM_STORAGE_KEY, merged);
  return merged;
}

function shouldDedupe(eventType) {
  return DEDUPED_EVENT_TYPES.has(eventType);
}

function makeDedupeKey(eventType, eventPageId, slug, ctaId) {
  const pageKey = coerceString(eventPageId) || coerceString(slug) || "unknown";
  return `${DEDUPE_PREFIX}:${eventType}:${pageKey}:${coerceString(ctaId) || "none"}:${window.location.pathname}`;
}

function wasRecentlyTracked(key) {
  if (!canUseBrowserApis()) return false;

  const now = Date.now();
  const memoryValue = memoryDedupe.get(key);
  if (memoryValue && now - memoryValue < DEDUPE_TTL_MS) return true;

  let storageValue = null;
  try {
    storageValue = Number(window.sessionStorage.getItem(key) || "0");
  } catch {
    storageValue = 0;
  }

  if (storageValue && now - storageValue < DEDUPE_TTL_MS) {
    memoryDedupe.set(key, storageValue);
    return true;
  }

  memoryDedupe.set(key, now);
  try {
    window.sessionStorage.setItem(key, String(now));
  } catch {
    return false;
  }
  return false;
}

function buildPayload(args) {
  const eventType = coerceString(args.eventType ?? args.event_type);
  if (!ALLOWED_EVENT_TYPES.has(eventType)) {
    throw new Error(`Unsupported event page analytics type: ${eventType || "unknown"}`);
  }

  const utm = persistUtmParams();
  const userAgent = coerceString(navigator.userAgent);

  return {
    event_page_id: trimToNull(args.eventPageId ?? args.event_page_id),
    slug: trimToNull(args.slug),
    event_type: eventType,
    cta_id: trimToNull(args.ctaId ?? args.cta_id),
    cta_label: trimToNull(args.ctaLabel ?? args.cta_label),
    session_id: getSessionId(),
    referer: trimToNull(args.referer) || trimToNull(document.referrer),
    utm_source: trimToNull(args.utm_source) || utm.utm_source || null,
    utm_medium: trimToNull(args.utm_medium) || utm.utm_medium || null,
    utm_campaign: trimToNull(args.utm_campaign) || utm.utm_campaign || null,
    utm_term: trimToNull(args.utm_term) || utm.utm_term || null,
    utm_content: trimToNull(args.utm_content) || utm.utm_content || null,
    device_type: getDeviceType(userAgent),
    user_agent: userAgent || null,
  };
}

function deliver(payload) {
  if (!canUseBrowserApis()) return;

  const body = JSON.stringify(payload);

  try {
    debugLog("dispatch", payload.event_type, {
      event_page_id: payload.event_page_id,
      slug: payload.slug,
    });
    fetch(ANALYTICS_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
      keepalive: true,
      credentials: "same-origin",
    }).catch((error) => {
      debugWarn("delivery failed", error);
    });
  } catch (error) {
    debugWarn("delivery failed", error);
  }
}

export function trackEventPageEvent(args = {}) {
  try {
    const payload = buildPayload(args);
    if (!payload.event_page_id && !payload.slug) return;

    if (shouldDedupe(payload.event_type)) {
      const dedupeKey = makeDedupeKey(
        payload.event_type,
        payload.event_page_id,
        payload.slug,
        payload.cta_id
      );
      if (wasRecentlyTracked(dedupeKey)) return;
    }

    deliver(payload);
  } catch (error) {
    debugWarn("skipped event", error);
  }
}

export function trackEventPageView(args = {}) {
  trackEventPageEvent({
    ...args,
    eventType: "page_view",
  });
}
